import { useAuth } from "@/contexts/AuthContext";
import { hasToolAccess, TOOL_ACCESS, PRICING, PlanTier, TIER_ORDER } from "@/config/pricing";
import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { useCredits } from "@/hooks/useCredits";

interface ToolGateProps {
  toolKey: string;
  children: React.ReactNode;
}

/**
 * Wraps a tool page and shows an upgrade prompt if the user's plan doesn't include access.
 */
export function ToolGate({ toolKey, children }: ToolGateProps) {
  const { subscription, isAdmin } = useAuth();
  const { planTier } = useCredits();

  // Admins always have full access
  if (isAdmin) {
    return <>{children}</>;
  }

  // Prioritize subscription.planTier (from Stripe/Whop) over credits DB tier
  // because user_credits.plan_tier can be stale "free" for users who subscribed after initial signup
  const userTier = (
    subscription.isLifetime ? "lifetime" :
    subscription.subscribed && subscription.planTier ? subscription.planTier :
    planTier || subscription.planTier || "free"
  ) as PlanTier | "lifetime";
  
  if (hasToolAccess(userTier, toolKey)) {
    return <>{children}</>;
  }

  const requiredTier = TOOL_ACCESS[toolKey] as PlanTier;
  const requiredPlan = requiredTier ? PRICING[requiredTier] : null;

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-6">
      <div className="text-center max-w-md space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-muted/50 border border-border flex items-center justify-center mx-auto">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="font-display text-2xl font-bold">Upgrade Required</h2>
        <p className="text-muted-foreground">
          This tool requires the <span className="text-primary font-semibold">{requiredPlan?.name || "higher"}</span> plan or above.
        </p>
        <Link
          to="/checkout"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity"
        >
          Upgrade Now — From ${requiredPlan?.price || 39}/mo
        </Link>
        <p className="text-xs text-muted-foreground">
          You're currently on the <span className="font-medium capitalize">{userTier}</span> plan
        </p>
      </div>
    </div>
  );
}
